import { SkeletonPlaceholder } from "@expert/common-ui";
import type { MantineColor } from "@mantine/core";
import { type BoxProps, type ElementProps, type MantineSpacing, Stack, Text, Transition } from "@mantine/core";
import type { ComponentType, ReactNode } from "react";
import type { ChatTask } from "@/sdk/agent/chat";
import { AboutToEnd } from "../CustomerSections";
import { CustomerName } from "../CustomerSections/CustomerName";
import classes from "../chat-components.module.css";
import { IncomingChatCopy } from "./IncomingChatCopy";
import { useFormattedTimestamp } from "./useFormattedTimeStamp";
import type { ChatVariant } from "./variants";

export interface ChatPreviewProps {
    chat: ChatTask;
    lastMessageDate: Date | null;
    variant: ChatVariant;
    variantColor: MantineColor;
}

const mapVariantToPreview = ({
    chat: { changeStateAt, customerName },
    formattedTimestamp,
    variant,
    variantColor,
}: ChatPreviewProps & { formattedTimestamp: string }): ReactNode => {
    const showSkeleton = (customerName.loading || customerName.value === undefined) && variant === "default";

    const customerInfo = (
        <>
            <SkeletonPlaceholder
                height={showSkeleton ? "var(--mantine-font-size-xs)" : "auto"}
                width={"90%"}
                visible={showSkeleton}
            />
            <SkeletonPlaceholder
                height={showSkeleton ? "var(--mantine-font-size-xxs)" : "auto"}
                mt={showSkeleton ? 6 : 0}
                width={showSkeleton ? "70%" : "90%"}
                visible={showSkeleton}
            />
            <Transition mounted={!showSkeleton} transition="fade-right" duration={300} timingFunction="ease">
                {(transitionStyle) => (
                    <Stack style={{ ...transitionStyle }} className={classes.chat_preview_builder}>
                        <CustomerName customerName={customerName} size="sm" truncate={true} />
                        <Text span fw="400" size="xxs" c="dimmed">
                            {formattedTimestamp}
                        </Text>
                    </Stack>
                )}
            </Transition>
        </>
    );

    switch (variant) {
        case "incoming":
            return (
                <>
                    {customerInfo}
                    <IncomingChatCopy />
                </>
            );
        case "neglected":
            return (
                <>
                    {customerInfo}
                    <AboutToEnd endReason="Neglected" endAt={changeStateAt} endReasonColor={variantColor} />
                </>
            );
        case "inactive":
            return (
                <>
                    {customerInfo}
                    <AboutToEnd endReason="Inactive" endAt={changeStateAt} endReasonColor={variantColor} />
                </>
            );
        case "closedByCustomer":
        case "closedByExpert":
            return (
                <>
                    {customerInfo}
                    <AboutToEnd endReason="Chat ended" endAt={changeStateAt} endReasonColor={variantColor} />
                </>
            );
        case "default":
        default:
            return <>{customerInfo}</>;
    }
};

interface ChatPreviewBuilderProps extends ChatPreviewProps {
    wrapper: ComponentType<BoxProps & ElementProps<"div"> & { gap?: MantineSpacing }>;
}

export function ChatPreviewBuilder({
    chat,
    lastMessageDate,
    variant,
    variantColor,
    wrapper: Wrapper = Stack,
}: ChatPreviewBuilderProps) {
    const formattedTimestamp = useFormattedTimestamp(lastMessageDate);
    const chatPreview = mapVariantToPreview({
        chat,
        lastMessageDate,
        formattedTimestamp,
        variant,
        variantColor,
    });

    return <Wrapper className={classes.chat_preview_builder}>{chatPreview}</Wrapper>;
}
